/**
 * Design Modifier Service
 * Applies user customizations (background color, text, logo) to design JSON
 * before loading into PitchPrint editor via load-design event.
 *
 * @version 1.0.0
 * @requires design-template-cache.js for cached design JSON
 */
(function() {
  'use strict';

  var DEBUG = false;
  var log = function() {
    if (DEBUG) console.log.apply(console, ['[DesignModifier]'].concat(Array.prototype.slice.call(arguments)));
  };

  var DesignModifier = {
    /**
     * Apply all customizations to design JSON
     * @param {Object} designJson - Design data with pages array
     * @param {Object} customizations - User customization selections
     * @param {string} [customizations.backgroundColor] - Hex color for background
     * @param {Array} [customizations.textEdits] - Array of {targetIndex, text} objects
     * @param {string} [customizations.logoUrl] - URL of uploaded logo image
     * @param {Object} [customizations.logoPosition] - {x, y, width, height} for logo
     * @param {Object} [customizations.pages] - Per-page customizations for double-sided
     * @returns {Object} Modified design JSON (deep cloned)
     */
    apply: function(designJson, customizations) {
      if (!designJson || !designJson.pages || !designJson.pages.length) {
        log('No pages to modify');
        return designJson;
      }

      if (!customizations || Object.keys(customizations).length === 0) {
        log('No customizations to apply');
        return designJson;
      }

      // Deep clone to avoid mutating original
      var modified = JSON.parse(JSON.stringify(designJson));

      // Check if we have per-page customizations (for double-sided)
      if (customizations.pages && Array.isArray(customizations.pages)) {
        modified.pages.forEach(function(page, pageIndex) {
          var pageCustomizations = customizations.pages[pageIndex];
          if (pageCustomizations) {
            DesignModifier._applyToPage(page, pageCustomizations);
          }
        });
      } else {
        // Apply same customizations to all pages (or just first page)
        modified.pages.forEach(function(page, pageIndex) {
          DesignModifier._applyToPage(page, customizations);
        });
      }

      log('Applied customizations:', customizations);
      return modified;
    },

    /**
     * Apply customizations to a single page
     * @private
     */
    _applyToPage: function(pageJson, customizations) {
      if (customizations.backgroundColor) {
        this.applyBackgroundColor(pageJson, customizations.backgroundColor);
      }

      if (customizations.textEdits && customizations.textEdits.length > 0) {
        this.applyTextEdits(pageJson, customizations.textEdits);
      }

      if (customizations.logoUrl) {
        this.applyLogo(pageJson, customizations.logoUrl, customizations.logoPosition);
      }
    },

    /**
     * Apply background color change to a page
     * @param {Object} pageJson - Page JSON object
     * @param {string} color - Hex color value (e.g., '#FFFFFF')
     */
    applyBackgroundColor: function(pageJson, color) {
      var canvas = pageJson.canvas || pageJson;
      var objects = canvas._objects || canvas.objects || [];

      // Find background object
      var bgInfo = this._findBackground(objects, canvas);

      if (bgInfo) {
        // Update existing background
        objects[bgInfo.index].fill = color;
        log('Applied background color:', color, 'at index', bgInfo.index);
      } else {
        // Create new background rect at the beginning
        var canvasWidth = canvas.width || (canvas.canvasDimension ? canvas.canvasDimension.pixels.fWidth : 1000);
        var canvasHeight = canvas.height || (canvas.canvasDimension ? canvas.canvasDimension.pixels.fHeight : 1000);

        var bgRect = {
          type: 'rect',
          left: 0,
          top: 0,
          width: canvasWidth,
          height: canvasHeight,
          fill: color,
          selectable: false,
          evented: false,
          name: 'background',
          excludeFromExport: false
        };

        // Insert at beginning so it's behind everything
        objects.unshift(bgRect);
        log('Created new background with color:', color);
      }
    },

    /**
     * Apply text edits to a page
     * @param {Object} pageJson - Page JSON object
     * @param {Array} textEdits - Array of edit objects
     * @param {number} textEdits[].targetIndex - Index in sorted text objects (0 = first/top)
     * @param {string} textEdits[].text - New text content
     * @param {string} [textEdits[].fill] - Optional text color
     */
    applyTextEdits: function(pageJson, textEdits) {
      var canvas = pageJson.canvas || pageJson;
      var objects = canvas._objects || canvas.objects || [];
      var textObjects = this._findTextObjects(objects);

      if (textObjects.length === 0) {
        log('No text objects found in page');
        return;
      }

      textEdits.forEach(function(edit) {
        var targetIndex = edit.targetIndex !== undefined ? edit.targetIndex : 0;
        var target = textObjects[targetIndex];

        if (target && edit.text !== undefined && edit.text !== null) {
          objects[target.index].text = edit.text;
          log('Applied text edit at index', target.index, ':', edit.text.substring(0, 20) + '...');

          // Optional: apply text color
          if (edit.fill) {
            objects[target.index].fill = edit.fill;
          }
        } else {
          log('Text target not found:', targetIndex, '(have', textObjects.length, 'text objects)');
        }
      });
    },

    /**
     * Apply logo/image to a page
     * @param {Object} pageJson - Page JSON object
     * @param {string} logoUrl - URL of the logo image
     * @param {Object} [position] - Optional position override
     * @param {number} [position.x] - X position
     * @param {number} [position.y] - Y position
     * @param {number} [position.width] - Width
     * @param {number} [position.height] - Height
     */
    applyLogo: function(pageJson, logoUrl, position) {
      var canvas = pageJson.canvas || pageJson;
      var objects = canvas._objects || canvas.objects || [];

      // Find existing logo placeholder
      var placeholder = this._findLogoPlaceholder(objects);

      // Default position (center-top area)
      var canvasWidth = canvas.width || 1000;
      var canvasHeight = canvas.height || 1000;

      var logoObject = {
        type: 'image',
        src: logoUrl,
        crossOrigin: 'anonymous', // Required for CORS
        originX: 'center',
        originY: 'center',
        name: 'user-logo',
        selectable: true,
        evented: true
      };

      if (placeholder) {
        // Use placeholder's position and approximate size
        logoObject.left = placeholder.object.left + (placeholder.object.width || 100) / 2;
        logoObject.top = placeholder.object.top + (placeholder.object.height || 100) / 2;
        logoObject.scaleX = 1;
        logoObject.scaleY = 1;

        // If placeholder has specific dimensions, use them to calculate scale
        if (placeholder.object.width && placeholder.object.height) {
          // We'll set a reasonable default scale, actual fitting happens in editor
          logoObject.scaleX = 0.5;
          logoObject.scaleY = 0.5;
        }

        // Replace placeholder with logo
        objects[placeholder.index] = logoObject;
        log('Replaced logo placeholder at index', placeholder.index);
      } else {
        // Use provided position or default
        if (position) {
          logoObject.left = position.x !== undefined ? position.x : canvasWidth * 0.5;
          logoObject.top = position.y !== undefined ? position.y : canvasHeight * 0.2;
          logoObject.scaleX = position.scaleX || 0.5;
          logoObject.scaleY = position.scaleY || 0.5;
        } else {
          // Default: center-top
          logoObject.left = canvasWidth * 0.5;
          logoObject.top = canvasHeight * 0.2;
          logoObject.scaleX = 0.5;
          logoObject.scaleY = 0.5;
        }

        // Add logo at end (on top of other elements)
        objects.push(logoObject);
        log('Added logo at end of objects array');
      }
    },

    /**
     * Analyze a design to find customizable elements
     * @param {Object} designJson - Design data with pages array
     * @returns {Object} Analysis results with backgrounds, text, and images
     */
    analyze: function(designJson) {
      if (!designJson || !designJson.pages || !designJson.pages.length) {
        return null;
      }

      var analysis = {
        pages: []
      };

      designJson.pages.forEach(function(page, pageIndex) {
        var canvas = page.canvas || page;
        var objects = canvas._objects || canvas.objects || [];

        var pageAnalysis = {
          pageIndex: pageIndex,
          backgrounds: [],
          textObjects: [],
          imageObjects: [],
          totalObjects: objects.length
        };

        objects.forEach(function(obj, objIndex) {
          var name = (obj.name || obj.id || '').toLowerCase();

          if (obj.type === 'rect') {
            // Check if it's a background
            if (name.includes('background') || name.includes('bg') ||
                (obj.left <= 0 && obj.top <= 0 && obj.width > 500)) {
              pageAnalysis.backgrounds.push({
                index: objIndex,
                fill: obj.fill,
                width: obj.width,
                height: obj.height
              });
            }
          } else if (['i-text', 'text', 'textbox'].includes(obj.type)) {
            pageAnalysis.textObjects.push({
              index: objIndex,
              text: obj.text,
              fontSize: obj.fontSize,
              fontFamily: obj.fontFamily,
              fill: obj.fill,
              top: obj.top,
              left: obj.left,
              width: obj.width,
              charLimit: obj.text ? obj.text.length + 20 : 50 // Suggest reasonable limit
            });
          } else if (obj.type === 'image') {
            pageAnalysis.imageObjects.push({
              index: objIndex,
              src: obj.src,
              name: obj.name || obj.id,
              isLogo: name.includes('logo') || name.includes('placeholder'),
              left: obj.left,
              top: obj.top,
              width: obj.width * (obj.scaleX || 1),
              height: obj.height * (obj.scaleY || 1)
            });
          }
        });

        // Sort text by vertical position (top to bottom)
        pageAnalysis.textObjects.sort(function(a, b) {
          return (a.top || 0) - (b.top || 0);
        });

        analysis.pages.push(pageAnalysis);
      });

      return analysis;
    },

    // =====================
    // Private Helper Methods
    // =====================

    /**
     * Find background object in canvas objects array
     * @private
     */
    _findBackground: function(objects, canvas) {
      // Strategy 1: Find by name/id containing 'background' or 'bg'
      for (var i = 0; i < objects.length; i++) {
        var obj = objects[i];
        var name = (obj.name || obj.id || '').toLowerCase();
        if (name.includes('background') || name === 'bg') {
          return { index: i, object: obj };
        }
      }

      // Strategy 2: Find first rect at origin that covers most of canvas
      var canvasWidth = canvas.width || canvas.canvasDimension?.pixels?.fWidth || 1000;
      var canvasHeight = canvas.height || canvas.canvasDimension?.pixels?.fHeight || 1000;

      for (var i = 0; i < objects.length; i++) {
        var obj = objects[i];
        if (obj.type === 'rect' && obj.left <= 0 && obj.top <= 0) {
          // Check if it spans most of canvas (at least 90%)
          if (obj.width >= canvasWidth * 0.9 && obj.height >= canvasHeight * 0.9) {
            return { index: i, object: obj };
          }
        }
      }

      // Strategy 3: First rect if nothing else found
      for (var i = 0; i < objects.length; i++) {
        if (objects[i].type === 'rect') {
          return { index: i, object: objects[i] };
        }
      }

      return null;
    },

    /**
     * Find all text objects in canvas, sorted by vertical position
     * @private
     */
    _findTextObjects: function(objects) {
      var textObjects = [];

      for (var i = 0; i < objects.length; i++) {
        var obj = objects[i];
        if (['i-text', 'text', 'textbox'].includes(obj.type)) {
          textObjects.push({
            index: i,
            object: obj,
            text: obj.text,
            top: obj.top || 0
          });
        }
      }

      // Sort by Y position (top to bottom) - this makes targeting predictable
      // First text object = headline, second = subtitle, etc.
      textObjects.sort(function(a, b) {
        return a.top - b.top;
      });

      return textObjects;
    },

    /**
     * Find logo placeholder in canvas
     * @private
     */
    _findLogoPlaceholder: function(objects) {
      for (var i = 0; i < objects.length; i++) {
        var obj = objects[i];
        var name = (obj.name || obj.id || '').toLowerCase();

        // Look for explicit logo or placeholder naming
        if (name.includes('logo') || name.includes('placeholder') || name.includes('upload')) {
          return { index: i, object: obj };
        }
      }

      // Also check for images that might be placeholder images
      for (var i = 0; i < objects.length; i++) {
        var obj = objects[i];
        if (obj.type === 'image') {
          // Check if it's a placeholder image (often has generic names or placeholder URLs)
          var src = (obj.src || '').toLowerCase();
          if (src.includes('placeholder') || src.includes('logo-here') || src.includes('upload')) {
            return { index: i, object: obj };
          }
        }
      }

      return null;
    },

    /**
     * Validate customizations before applying
     * @param {Object} customizations - Customization object to validate
     * @returns {Object} {valid: boolean, errors: string[]}
     */
    validate: function(customizations) {
      var errors = [];

      if (customizations.backgroundColor) {
        // Validate hex color format
        if (!/^#[0-9A-Fa-f]{6}$/.test(customizations.backgroundColor)) {
          errors.push('Invalid background color format. Use hex format like #FFFFFF');
        }
      }

      if (customizations.textEdits) {
        if (!Array.isArray(customizations.textEdits)) {
          errors.push('textEdits must be an array');
        } else {
          customizations.textEdits.forEach(function(edit, i) {
            if (edit.targetIndex !== undefined && typeof edit.targetIndex !== 'number') {
              errors.push('textEdits[' + i + '].targetIndex must be a number');
            }
            if (typeof edit.text !== 'string') {
              errors.push('textEdits[' + i + '].text must be a string');
            }
          });
        }
      }

      if (customizations.logoUrl) {
        if (typeof customizations.logoUrl !== 'string') {
          errors.push('logoUrl must be a string');
        }
        // Basic URL validation
        if (!/^https?:\/\/.+/.test(customizations.logoUrl)) {
          errors.push('logoUrl must be a valid URL starting with http:// or https://');
        }
      }

      return {
        valid: errors.length === 0,
        errors: errors
      };
    },

    /**
     * Enable/disable debug logging
     */
    setDebug: function(enabled) {
      DEBUG = !!enabled;
    }
  };

  // Expose globally
  window.DesignModifier = DesignModifier;
  log('Design Modifier initialized');
})();
